// Shared vocabulary + tiny helpers for the Disputes Center
// (Customer Service > Disputes). Status/type values mirror the backend's
// routes/admin/disputes/helpers.js; colors come from the semantic family
// system (utils/semanticColors.js) — no new hexes.

import React from "react";
import { familyByKey } from "../../../utils/semanticColors";

// status → { label, family } (semantic family key)
export const STATUS_META = {
  needs_response: { label: "Needs response", family: "orange" },
  under_review: { label: "Under review", family: "yellow" },
  responded: { label: "Responded", family: "blue" },
  warning_needs_response: { label: "Inquiry — respond", family: "orange" },
  warning_under_review: { label: "Inquiry — review", family: "yellow" },
  warning_closed: { label: "Inquiry closed", family: "gray" },
  won: { label: "Won", family: "green" },
  lost: { label: "Lost", family: "red" },
  accepted: { label: "Accepted", family: "red" },
  // Negotiated middle — part conceded (settled_amount_cents), rest kept.
  settled: { label: "Settled", family: "blue" },
  charge_refunded: { label: "Refunded", family: "gray" },
  draft: { label: "Draft", family: "gray" },
  // Response window passed unanswered (nightly sweep) — no longer in the
  // open worklist, but the processor's won/lost decision is still pending.
  expired: { label: "Expired — awaiting outcome", family: "purple" },
  // Staff deliberately chose not to fight this one — letting the window run
  // out. Out of the worklist; the outcome records when the bank decides.
  not_contesting: { label: "Not contesting", family: "gray" },
  resolved: { label: "Resolved", family: "gray" },
};

export const OPEN_STATUS_KEYS = [
  "needs_response",
  "under_review",
  "responded",
  "warning_needs_response",
  "warning_under_review",
];

// The case list's status chips (DisputesCenter.js): [key, label].
export const STATUS_CHIPS = [
  ["all", "All"],
  ["open", "Open"],
  ["needs_response", "Needs response"],
  ["under_review", "Under review"],
  ["responded", "Responded"],
  ["won", "Won"],
  ["lost", "Lost"],
  ["settled", "Settled"],
  ["expired", "Expired"],
  ["not_contesting", "Not contesting"],
  ["draft", "Drafts"],
];

// Shopmonkey work queues (server: ?queue=, counts from /stats) — chips that
// show while they hold a case: [key, label, stats count key, what it holds].
// Keys are prefixed so they never collide with a status chip.
export const QUEUE_CHIPS = [
  ["queue:needs_sending", "Needs sending", "needs_sending_count", "Open Shopmonkey cases nobody has sent evidence for yet"],
  ["queue:not_filed", "Not filed", "not_filed_count", "Evidence we emailed that Shopmonkey's feed doesn't show filed yet"],
  ["queue:to_review", "Losses to review", "to_review_count", "Shopmonkey cases we emailed evidence for and lost since Aug 31, 2026 that nobody has reviewed yet"],
];

// Open cases (and drafts) are a live worklist — a case filed before the
// selected range must never be hidden, so those views skip the date filter
// (which the KPIs treat as live too).
export const isLiveView = (chip) =>
  chip === "open" ||
  chip === "draft" ||
  chip === "attention" ||
  chip.startsWith("queue:") ||
  OPEN_STATUS_KEYS.includes(chip);

/**
 * The case list's request for what the page has picked, without page or
 * limit. The list (DisputesCenter.js) and the Print menu's "The list on
 * screen" (disputePrintLists.js) both send it, so the printout is the list.
 *   view: { statusChip, processor, location, sort, from, to, search }
 */
export function listParams({ statusChip, processor, location, sort, from, to, search }) {
  const qs = new URLSearchParams();
  // "Needs you" = cases the Plus autopilot handed to a person (any status).
  if (statusChip === "attention") qs.set("autopilot", "attention");
  else if (statusChip.startsWith("queue:")) qs.set("queue", statusChip.slice("queue:".length));
  else if (statusChip !== "all") qs.set("status", statusChip);
  if (processor) qs.set("processor", processor);
  if (location) qs.set("location", location);
  if (sort) {
    qs.set("sort", sort.key);
    qs.set("dir", sort.dir);
  }
  if (!isLiveView(statusChip)) {
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
  }
  if (search) qs.set("search", search);
  return qs;
}

export const TYPE_LABELS = {
  chargeback: "Chargeback",
  retrieval_request: "Retrieval request",
  inquiry: "Inquiry",
  pre_arbitration: "Pre-arbitration",
};

export const PROCESSOR_LABELS = {
  intuit: "Intuit",
  stripe: "Stripe",
  square: "Square",
  authorize_net: "Authorize.Net",
  whop: "Whop",
  shopmonkey: "Shopmonkey",
  paypal: "PayPal",
  paypal_2: "PayPal (account 2)",
  cardpointe: "Chase (CardPointe)",
  elavon: "Elavon",
  fluidpay: "FluidPay (Hawthorne)",
  other: "Other",
};

// Both PayPal business accounts: each answers only its own disputes
// (backend routes/admin/disputes/paypalApi.js).
export const isPayPal = (processor) => processor === "paypal" || processor === "paypal_2";

// No per-case deep link is documented for PayPal's Resolution Center — land
// on the center and find the case by its PP-… id.
export const paypalResolutionUrl = "https://www.paypal.com/resolutioncenter";

// Case number fit for display: Shopmonkey inquiry-phase cases have no real
// case id yet (no Stripe dispute exists) — their `sm-<payment id>` key is an
// internal sync handle, not something staff can look up anywhere.
export const displayCaseId = (id) => (id && !String(id).startsWith("sm-") ? id : null);

// Shopmonkey cases sync automatically from the EDS data stream (15-min cron);
// there's no per-case deep link, so the button lands on the Disputes tab —
// switch to the case's location once there.
export const shopmonkeyDisputesUrl = "https://app.shopmonkey.cloud/settings/payments?tab=disputes";

// The customer's own Shopmonkey page — their order history, vehicles and notes
// live there, none of which migrated to Chariot. The id is Shopmonkey's
// customer uuid, resolved server-side (see routes/admin/disputes/
// shopmonkeyCustomer.js); absent when the customer has no Shopmonkey record.
// The URL itself lives in utils/shopmonkey.js (Dig links to the same page).
export { shopmonkeyCustomerUrl } from "../../../utils/shopmonkey";

// Shopmonkey's dispute-department inbox — their team confirmed evidence can
// be emailed here instead of the login-gated ShareFile upload link. Editable
// in the compose modal; verify with Shopmonkey if sends ever bounce.
export const SHOPMONKEY_DISPUTES_EMAIL = "disputes@shopmonkey.io";

// Processors whose cases are FED BY A SYNC: status/amounts/outcome arrive
// automatically and manual outcome buttons are hidden (the sync would just
// overwrite them). Behavior checks go through this — a future synced feed
// (PayPal?) is one entry here, not a hunt for processor literals.
const SYNCED_PROCESSORS = ["shopmonkey"];
export const isSyncedProcessor = (p) => SYNCED_PROCESSORS.includes(p);

// A case OWNED by a sync feed (created via api_sync): outcome buttons hide
// and the synced banner shows. Keyed on source, not processor — a
// manually-logged case must stay fully operable.
export const isSyncedCase = (d) => d?.source === "api_sync";

// A case from Shopmonkey's own feed: the only kind whose Shopmonkey documents
// can be pulled and whose evidence kit can be built (the server's rule,
// shopmonkeyPull.js fromShopmonkeyFeed).
export const isShopmonkeyFeedCase = (d) => d?.processor === "shopmonkey" && isSyncedCase(d);

// A document the pull put on the case, by its exact name (the server's
// services/disputes/ownFiles.js isPulled): a staff file merely starting
// "Shopmonkey" isn't one.
const PULLED_RE = /^Shopmonkey (Signed Authorization - Order .+|Authorized Order .+|Inspection Report - Order .+)\.pdf$/;
export const isPulledDoc = (a) => PULLED_RE.test(a?.file_name || "");

// Which rows participate in the "new case" viewed lifecycle: sync-created
// rows nobody has opened yet. The ONE definition — row highlight and the
// mark-viewed POST must always agree (backend /:id/viewed accepts any id).
export const isUnseen = (r) => isSyncedCase(r) && !r?.viewed_at;

// The one explanation for an estimated deadline on a Shopmonkey case — shown
// wherever an "est." date appears (list, drawer header, details).
export const SM_DEADLINE_TOOLTIP =
  "Shopmonkey doesn't send its Respond By date, so this one is estimated from the card brand. Copy the real date from the Shopmonkey dispute into this case.";

// What the Shopmonkey data stream does NOT include — shown as the ⓘ tooltip
// on synced cases so staff know what to maintain by hand.
export const SM_NOT_STREAMED =
  "Synced automatically from Shopmonkey every 15 minutes: status, amounts, fee, reason, card, order, the won/lost outcome, and whether Shopmonkey has filed our evidence.\n\n" +
  "NOT streamed — review and update by hand:\n" +
  "• Respond-by deadline — estimated from the card brand until you copy the real date from the Shopmonkey dispute\n" +
  "• The evidence itself — email it from here (it marks the case responded), or upload it in Shopmonkey and mark responded here";

// The deadline a case works to: the real Respond By, else the server's
// estimate for a Shopmonkey case still waiting on us (respond_by_est —
// services/disputes/deadlines.js). { day: 'YYYY-MM-DD', estimated } | null
export const caseDeadline = (d) => {
  const raw = d?.respond_by || d?.respond_by_est;
  const m = raw && String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? { day: `${m[1]}-${m[2]}-${m[3]}`, estimated: !d.respond_by } : null;
};

// Where a Shopmonkey case stands with its evidence — the server's `filing`
// (services/disputes/deadlines.js filingStateOf). Tones are .dsp-due's.
// 'unsent' has no chip: the status pill already says nothing went out.
export const FILING_META = {
  waiting: {
    label: "Sent · waiting on Shopmonkey",
    tone: "ok",
    title: "Emailed to Shopmonkey — they take 24–48 hours to file it with the bank",
  },
  late: {
    label: "Not filed yet",
    tone: "overdue",
    title: "We emailed the evidence, but Shopmonkey's feed doesn't show it filed — ask disputes@shopmonkey.io to file it",
  },
  filed: { label: "Filed by Shopmonkey", tone: "ok", title: "Shopmonkey's feed shows the evidence filed with the bank" },
};

// Normalized reasons (Stripe vocabulary — Intuit notices map onto these).
export const REASON_OPTIONS = [
  ["fraudulent", "Fraudulent / not authorized"],
  ["unrecognized", "Unrecognized charge"],
  ["subscription_canceled", "Subscription canceled"],
  ["product_not_received", "Service not received"],
  ["product_unacceptable", "Service unacceptable"],
  ["duplicate", "Duplicate charge"],
  ["credit_not_processed", "Credit not processed"],
  ["general", "General / other"],
];

// A case's reason in words: ours, else the processor's own text.
export const reasonLabel = (reason, reasonText) => {
  const found = REASON_OPTIONS.find(([k]) => k === reason);
  return found ? found[1] : reasonText || reason || "—";
};

// Attachment buckets — mirror the QBO Resolution Center's upload sections.
export const ATTACHMENT_CATEGORY_LABELS = {
  notice: "Dispute notice",
  product_description: "Product / service description",
  invoice: "Invoice",
  correspondence: "Correspondence with cardholder",
  proof_of_satisfaction: "Proof of satisfaction",
  receipt: "Receipt",
  explanation: "Explanation / rebuttal",
  other: "Other",
};

// What tends to win a card-not-present dispute, by reason. Shown as a
// checklist in the drawer so whoever responds gathers the right documents.
const BASE_EVIDENCE = [
  "Invoice / receipt for the transaction",
  "Proof the service was performed (work order, photos, sign-off)",
  "Communication with the customer (emails, texts, calls)",
];
export const EVIDENCE_CHECKLIST = {
  fraudulent: [
    "Signed estimate approval or membership signup record",
    "Terms-of-service / billing acceptance (date + who accepted)",
    "Prior undisputed charges on the same card (Visa CE 3.0)",
    ...BASE_EVIDENCE,
  ],
  unrecognized: [
    "Statement descriptor explanation (what the charge shows as)",
    ...BASE_EVIDENCE,
  ],
  subscription_canceled: [
    "Membership agreement + billing terms the customer accepted",
    "Cancellation policy and any cancellation records (or lack of one)",
    "Membership usage/benefit history",
    ...BASE_EVIDENCE,
  ],
  product_not_received: [
    "Completed work order + inspection photos",
    "Customer sign-off or completion confirmation",
    ...BASE_EVIDENCE,
  ],
  product_unacceptable: [
    "Work order detail + inspection photos showing work quality",
    "Any complaint history and how it was addressed",
    ...BASE_EVIDENCE,
  ],
  duplicate: [
    "Both transaction records showing distinct services/dates",
    ...BASE_EVIDENCE,
  ],
  credit_not_processed: [
    "Refund policy as disclosed to the customer",
    "Refund/credit records, or rationale for refusing",
    ...BASE_EVIDENCE,
  ],
  general: BASE_EVIDENCE,
};

// Deep link to the case in Intuit's QBO Resolution Center (case number based).
export const intuitCaseUrl = (caseId) =>
  `https://qbo.intuit.com/app/case?domain=payments&id=${encodeURIComponent(caseId)}`;

export const fmtMoney = (cents) =>
  cents == null
    ? "—"
    : (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

// Calendar-date extraction. DATE columns and date-input values arrive as
// "YYYY-MM-DD…" ISO strings whose UTC date part IS the intended calendar day
// (they were stored as midnight UTC) — parsing them through `new Date()` and
// rendering in local time shifts them a day for US-timezone users when the
// server runs UTC. Pull the day out of the string instead.
const calendarParts = (v) => {
  const m = typeof v === "string" && v.match(/^(\d{4})-(\d{2})-(\d{2})(T00:00:00(\.000)?Z)?$/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};

export const fmtDate = (d) => {
  if (!d) return "—";
  const dt = calendarParts(d) || new Date(d);
  return Number.isNaN(dt.getTime())
    ? "—"
    : dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// An instant to the minute, in the viewer's time: "Oct 27, 3:05 PM".
export const fmtWhen = (iso) => {
  const dt = iso ? new Date(iso) : null;
  return !dt || Number.isNaN(dt.getTime())
    ? "—"
    : dt.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

// A file's size: "1.2 MB", or under a tenth of a megabyte "44 KB" (never "0.0 MB").
export const fmtMb = (bytes) => {
  const n = Number(bytes || 0);
  return n > 0 && n < 0.1 * 1048576 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1048576).toFixed(1)} MB`;
};

// How an evidence email stood against its deadline, from the server's
// { day, estimated, days_before } (services/disputes/deadlines.js):
// "3 days before the estimated Respond By date (Oct 4, 2026)". Null without one.
export const daysBeforePhrase = (deadline) => {
  const n = deadline?.days_before;
  if (!Number.isInteger(n) || !deadline.day) return null;
  const when =
    n > 0 ? `${n} day${n === 1 ? "" : "s"} before` : n === 0 ? "on the day of" : `${-n} day${n === -1 ? "" : "s"} after`;
  return `${when} the ${deadline.estimated ? "estimated " : ""}Respond By date (${fmtDate(deadline.day)})`;
};

// Respond-by urgency for an OPEN dispute:
// { label, tone: 'overdue'|'urgent'|'soon'|'ok', estimated, days } — days
// until the deadline, negative once it's passed.
// Compared at CALENDAR-DAY granularity — a case is not "overdue" until the
// day AFTER its respond-by date, regardless of timezone.
export const dueMeta = (dispute) => {
  if (!dispute || dispute.closed_at) return null;
  if (dispute.status === "draft") return null; // not logged yet — no clock
  if (dispute.status === "expired") return null; // window passed — the pill says it
  if (dispute.status === "not_contesting") return null; // deliberate lapse — no nagging
  if (dispute.responded_at) return null; // already responded — deadline met
  // Shopmonkey doesn't send its Respond By date: until staff copy it in, a
  // case waiting on us works to the server's estimate (Allen's call Oct 27
  // 2026, replacing the old "Due soon" placeholder). NOTE the server twin:
  // stats.js counts due-soon / overdue on the same estimate.
  const deadline = caseDeadline(dispute);
  if (!deadline) return null;
  const [y, mo, d] = deadline.day.split("-").map(Number);
  const due = new Date(y, mo - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((due - today) / 86400000);
  const est = deadline.estimated ? " (est.)" : "";
  const meta = (label, tone) => ({ label: `${label}${est}`, tone, estimated: deadline.estimated, days });
  if (days < 0) return meta("OVERDUE", "overdue");
  if (days === 0) return meta("DUE TODAY", "overdue");
  if (days < 5) return meta(`Due in ${days}d`, "urgent");
  if (days < 10) return meta(`Due in ${days}d`, "soon");
  return meta(`Due in ${days}d`, "ok");
};

export function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status, family: "gray" };
  const fam = familyByKey(meta.family);
  return (
    <span
      className="dsp-pill"
      style={{ background: fam.light, color: fam.vivid, borderColor: fam.vivid }}
    >
      {meta.label}
    </span>
  );
}

// Distinct badge for Shopmonkey-synced cases so they read apart from Chariot
// (Intuit/Stripe) cases at a glance; hover explains what isn't streamed.
export function ShopmonkeyBadge({ locationLabel }) {
  const fam = familyByKey("purple");
  return (
    <span
      className="dsp-pill"
      style={{ background: fam.light, color: fam.vivid, borderColor: fam.vivid }}
      title={SM_NOT_STREAMED}
    >
      Shopmonkey{locationLabel ? ` · ${locationLabel}` : ""}
    </span>
  );
}
